import { useEffect, useRef } from "react";
import socket from "../Sales1/socket";

const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes — same as Microsoft Teams

/**
 * Detects user idle state and emits auto-away/auto-return via socket.
 * Listens for mouse, keyboard, scroll, touch, and visibility changes.
 */
export default function useIdleDetector(userId) {
  const idleTimerRef = useRef(null);
  const isIdleRef = useRef(false);

  useEffect(() => {
    if (!userId) return;

    const setActive = () => {
      // User is active — clear idle timer and return from away if needed
      clearTimeout(idleTimerRef.current);

      if (isIdleRef.current) {
        isIdleRef.current = false;
        socket.emit("chat:auto-return", { userId });
      }

      // Restart idle countdown
      idleTimerRef.current = setTimeout(() => {
        isIdleRef.current = true;
        socket.emit("chat:auto-away", { userId });
      }, IDLE_TIMEOUT);
    };

    // Tab visibility change (switching browser tabs, minimizing window)
    const handleVisibility = () => {
      if (document.hidden) {
        // Start shorter idle timer when tab is hidden (2 minutes)
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
          isIdleRef.current = true;
          socket.emit("chat:auto-away", { userId });
        }, 2 * 60 * 1000);
      } else {
        setActive();
      }
    };

    // Listen for activity events
    const events = ["mousemove", "keydown", "scroll", "touchstart", "click"];
    events.forEach((event) => window.addEventListener(event, setActive, { passive: true }));
    document.addEventListener("visibilitychange", handleVisibility);

    // Start initial timer
    setActive();

    return () => {
      clearTimeout(idleTimerRef.current);
      events.forEach((event) => window.removeEventListener(event, setActive));
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [userId]);
}
